import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, TextField, Button, Avatar,
  Alert, Snackbar, CircularProgress, Chip,
} from '@mui/material';

export default function AdminProfilePage() {
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [profile, setProfile] = useState({ email: '', role: '' });

  // Password fields
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload;
        setProfile({
          email: payload?.email || '',
          role: payload?.['custom:role'] || 'platform_admin',
        });
      } catch (e) {
        setToast({ open: true, message: 'Failed to load profile.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setToast({ open: true, message: 'New passwords do not match.', severity: 'error' });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setToast({ open: true, message: 'Password must be at least 8 characters.', severity: 'error' });
      return;
    }
    setChangingPassword(true);
    try {
      const { updatePassword } = await import('aws-amplify/auth');
      await updatePassword({
        oldPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setToast({ open: true, message: 'Password changed successfully.', severity: 'success' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (e) {
      const msg = e.name === 'NotAuthorizedException'
        ? 'Current password is incorrect.'
        : e.message;
      setToast({ open: true, message: msg, severity: 'error' });
    } finally {
      setChangingPassword(false);
    }
  };

  const roleLabel = profile.role === 'platform_manager' ? 'Manager'
    : profile.role === 'platform_superadmin' ? 'Super Admin' : 'Admin';

  if (loading) {
    return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 700 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>Profile</Typography>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={3} alignItems="center" mb={3}>
          <Avatar sx={{ width: 64, height: 64, fontSize: 28, bgcolor: 'primary.main' }}>
            {(profile.email || '?')[0].toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h6">{profile.email}</Typography>
            <Chip
              label={roleLabel}
              size="small"
              color={profile.role === 'platform_manager' ? 'warning' : 'primary'}
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Stack>

        <TextField
          label="Email"
          size="small"
          fullWidth
          value={profile.email}
          disabled
          helperText="Email is managed through the admin user pool."
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Change Password</Typography>
        <Stack spacing={2}>
          <TextField
            label="Current Password"
            type="password"
            size="small"
            fullWidth
            value={passwordForm.currentPassword}
            onChange={(e) => setPasswordForm((f) => ({ ...f, currentPassword: e.target.value }))}
          />
          <TextField
            label="New Password"
            type="password"
            size="small"
            fullWidth
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm((f) => ({ ...f, newPassword: e.target.value }))}
          />
          <TextField
            label="Confirm New Password"
            type="password"
            size="small"
            fullWidth
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm((f) => ({ ...f, confirmPassword: e.target.value }))}
            error={passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword}
            helperText={passwordForm.confirmPassword !== '' && passwordForm.newPassword !== passwordForm.confirmPassword ? 'Passwords do not match' : ''}
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleChangePassword}
              disabled={changingPassword || !passwordForm.currentPassword || !passwordForm.newPassword}
            >
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
